import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAgencies } from '../App/actions'
import SearchIcon from '../../images/search.svg'
import { ContentBox, BoxTitle, AgencyTableScroll } from './styledComponents'
import {
  TableCard, StyledTable, STHead, STH, STBody, STR, STD,
  PlayerCellWrapper, InitialBubble, NoneText,
} from '../SimuPlayers/styledComponents'

const getAgencyId = (agency) => String(agency.agencyId || agency._id || agency.id || '')
const getAgencyName = (agency) => agency.agencyName || agency.agencyname || ''
const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0][0] || '?').toUpperCase()
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase()
}

const toArray = (value) => {
  if (Array.isArray(value)) return value
  if (value && typeof value.toJS === 'function') {
    const js = value.toJS()
    return Array.isArray(js) ? js : []
  }
  return []
}

const AgencyProductAssignments = ({ formationId }) => {
  const dispatch = useDispatch()
  const agencytoken = useSelector(state => state.getIn(['global', 'agencytoken']))
  const agenciesState = useSelector(state => state.getIn(['global', 'agencies']))

  const agencies = useMemo(
    () => toArray(agenciesState),
    [agenciesState]
  )

  const [selectedAgencyIds, setSelectedAgencyIds] = useState([])
  const [agencySearch, setAgencySearch] = useState('')
  const [savingAgencies, setSavingAgencies] = useState(false)
  const [agencySaveError, setAgencySaveError] = useState('')
  const [agencySaveSuccess, setAgencySaveSuccess] = useState('')
  const productsApiBase = process.env.REACT_APP_TGDAPI_SERVER || process.env.REACT_APP_SERVER_API || ''

  const agencyRows = useMemo(() => {
    return agencies
      .map((agency) => ({
        id: getAgencyId(agency),
        name: getAgencyName(agency),
        email: agency.notificationEmail || agency.email || null,
        agencyProducts: Array.isArray(agency.agencyProducts) ? agency.agencyProducts.map(String) : [],
      }))
      .filter((row) => row.id && row.name)
      .sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
  }, [agencies])

  const filteredAgencyRows = useMemo(() => {
    const q = agencySearch.trim().toLowerCase()
    if (!q) return agencyRows
    return agencyRows.filter((row) => row.name.toLowerCase().includes(q) || (row.email || '').toLowerCase().includes(q))
  }, [agencyRows, agencySearch])

  const visibleAgencyIds = useMemo(() => filteredAgencyRows.map((row) => row.id), [filteredAgencyRows])

  const assignmentSyncKey = useMemo(() => {
    const productId = String(formationId)
    return agencyRows
      .map((row) => `${row.id}:${row.agencyProducts.includes(productId) ? '1' : '0'}`)
      .join('|')
  }, [agencyRows, formationId])

  const checkboxStyle = { width: '18px', height: '18px', cursor: 'pointer' }

  useEffect(() => {
    if (agencytoken && agencies.length === 0) {
      dispatch(getAgencies(agencytoken))
    }
  }, [dispatch, agencytoken, agencies.length])

  useEffect(() => {
    const productId = String(formationId)
    const assignedIds = agencyRows
      .filter((row) => row.agencyProducts.includes(productId))
      .map((row) => row.id)
    setSelectedAgencyIds(assignedIds)
  }, [formationId, assignmentSyncKey])

  useEffect(() => {
    setAgencySaveError('')
    setAgencySaveSuccess('')
  }, [formationId])

  const saveAgencyAssignments = async () => {
    if (!formationId || !agencytoken) {
      setAgencySaveError('Token manquant pour enregistrer les droits produit.')
      return
    }
    if (!productsApiBase) {
      setAgencySaveError('Configuration API manquante (REACT_APP_TGDAPI_SERVER ou REACT_APP_SERVER_API).')
      return
    }

    setAgencySaveError('')
    setAgencySaveSuccess('')
    setSavingAgencies(true)

    const productId = String(formationId)
    const selectedSet = new Set(selectedAgencyIds)

    const changedRows = agencyRows.filter((row) => {
      const currentlyAssigned = row.agencyProducts.includes(productId)
      const nextAssigned = selectedSet.has(row.id)
      return currentlyAssigned !== nextAssigned
    })

    if (changedRows.length === 0) {
      setSavingAgencies(false)
      setAgencySaveSuccess('Aucune modification a enregistrer.')
      return
    }

    try {
      await Promise.all(changedRows.map(async (row) => {
        const nextProducts = new Set(row.agencyProducts)
        if (selectedSet.has(row.id)) nextProducts.add(productId)
        else nextProducts.delete(productId)

        const response = await fetch(`${productsApiBase}/agencies/${row.id}/products`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${agencytoken}`,
          },
          body: JSON.stringify({ agencyProducts: Array.from(nextProducts) }),
        })

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '')
          throw new Error(errorBody || `HTTP ${response.status}`)
        }
      }))

      setAgencySaveSuccess(`Attribution enregistree pour ${changedRows.length} agence${changedRows.length > 1 ? 's' : ''}.`)
      dispatch(getAgencies(agencytoken))
    } catch (err) {
      setAgencySaveError(`Impossible d'enregistrer les attributions: ${err.message || 'erreur inconnue'}`)
    } finally {
      setSavingAgencies(false)
    }
  }

  return (
    <ContentBox>
      <BoxTitle>Attribution du produit aux agences</BoxTitle>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Poppins', fontSize: '0.86rem', color: '#6B7280' }}>
          Selectionnez les agences qui peuvent acceder a ce produit.
        </div>
        <div style={{ minWidth: '260px', flex: 1, maxWidth: '420px', display: 'flex', alignItems: 'center', gap: '0.45rem', background: '#fff', border: '1px solid #D8DCE8', borderRadius: '7px', padding: '0.35rem 0.55rem' }}>
          <img src={SearchIcon} alt='' style={{ width: '14px', height: '14px', opacity: 0.5 }} />
          <input
            type='text'
            value={agencySearch}
            onChange={(e) => setAgencySearch(e.target.value)}
            placeholder='Rechercher une agence ou un email'
            style={{ width: '100%', border: 'none', outline: 'none', fontFamily: 'Poppins', fontSize: '0.8rem', color: '#111827' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.45rem' }}>
          <button
            type='button'
            onClick={() => setSelectedAgencyIds((prev) => Array.from(new Set([...prev, ...visibleAgencyIds])))}
            disabled={visibleAgencyIds.length === 0}
            style={{ height: '2rem', borderRadius: '6px', border: '1px solid #D8DCE8', background: '#fff', fontFamily: 'Poppins', fontSize: '0.78rem', fontWeight: 600, color: '#374151', cursor: visibleAgencyIds.length === 0 ? 'not-allowed' : 'pointer', padding: '0 0.75rem' }}
          >
            Tout selectionner
          </button>
          <button
            type='button'
            onClick={() => setSelectedAgencyIds((prev) => prev.filter((id) => !visibleAgencyIds.includes(id)))}
            disabled={visibleAgencyIds.length === 0}
            style={{ height: '2rem', borderRadius: '6px', border: '1px solid #D8DCE8', background: '#fff', fontFamily: 'Poppins', fontSize: '0.78rem', fontWeight: 600, color: '#374151', cursor: visibleAgencyIds.length === 0 ? 'not-allowed' : 'pointer', padding: '0 0.75rem' }}
          >
            Tout deselectionner
          </button>
          <button
            type='button'
            onClick={saveAgencyAssignments}
            disabled={savingAgencies}
            style={{ height: '2rem', borderRadius: '6px', border: '1px solid #4465FE', background: savingAgencies ? '#C5CCFF' : '#8EA2FF', fontFamily: 'Poppins', fontSize: '0.78rem', fontWeight: 600, color: '#fff', cursor: savingAgencies ? 'not-allowed' : 'pointer', padding: '0 0.75rem' }}
          >
            {savingAgencies ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {agencySaveError && (
        <div style={{ fontFamily: 'Poppins', fontSize: '0.8rem', color: '#E53E3E', marginBottom: '0.6rem' }}>
          {agencySaveError}
        </div>
      )}
      {agencySaveSuccess && (
        <div style={{ fontFamily: 'Poppins', fontSize: '0.8rem', color: '#16A34A', marginBottom: '0.6rem' }}>
          {agencySaveSuccess}
        </div>
      )}

      <TableCard>
        <AgencyTableScroll>
          <StyledTable>
            <STHead>
              <tr>
                <STH style={{ width: '50%' }}>Agence</STH>
                <STH style={{ width: '35%' }}>Email</STH>
                <STH style={{ width: '15%', textAlign: 'center' }}>Autorise</STH>
              </tr>
            </STHead>
            <STBody>
              {filteredAgencyRows.map((row) => (
                <STR key={row.id}>
                  <STD>
                    <PlayerCellWrapper>
                      <InitialBubble>{getInitials(row.name)}</InitialBubble>
                      <span style={{ fontFamily: 'Poppins', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                        {row.name}
                      </span>
                    </PlayerCellWrapper>
                  </STD>
                  <STD>
                    {row.email
                      ? <span style={{ fontFamily: 'Poppins', fontSize: '0.78rem', color: '#6B7280' }}>{row.email}</span>
                      : <NoneText>-</NoneText>
                    }
                  </STD>
                  <STD style={{ textAlign: 'center' }}>
                    <input
                      type='checkbox'
                      checked={selectedAgencyIds.includes(row.id)}
                      onChange={() => setSelectedAgencyIds((prev) => (
                        prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]
                      ))}
                      aria-label={`Autoriser ${row.name}`}
                      style={checkboxStyle}
                    />
                  </STD>
                </STR>
              ))}
              {filteredAgencyRows.length === 0 && (
                <STR>
                  <STD colSpan={3} style={{ textAlign: 'center', color: '#9CA3AF' }}>
                    Aucune agence ne correspond a la recherche.
                  </STD>
                </STR>
              )}
            </STBody>
          </StyledTable>
        </AgencyTableScroll>
      </TableCard>
    </ContentBox>
  )
}

export default AgencyProductAssignments
